import { FC, Fragment, useCallback } from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import {
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { useToggle } from "rooks";
import Chip from "@mui/material/Chip";
import { DimensionWithCosts } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { isCategoryWithDimensionId } from "@/components/pages/dimensions/utils/helpers/isCategoryWithDimensionId";
import { AddDimensionButton } from "@/components/pages/dimensions/components/AddDimensionButton";
import { AddCategoryButtonIntoExistingDimension } from "@/components/pages/dimensions/components/data-grid/categories/AddCategoryButtonIntoExistingDimension";
import {
  CategoryWithDimensionId,
  DimensionsWithChildren,
} from "@/store/dimensions/utils/types/common";
import { useAppAbility } from "@/services/permissions";
import { useAppDispatch } from "@/store/hooks";
import { editDimensionOrCategoryThunk } from "@/store/dimensions/thunks/editDimensionOrCategoryThunk";
import { DimensionCategoryEditField } from "@/components/pages/dimensions/components/data-grid/dimensions/cell-renderers/DimensionCategoryEditField";
import { agGridGrandFooterColId } from "@/components/pages/common/ag-grid/utils/constants/agCommon";
import { useAGGridRowHoverHook } from "@/components/pages/common/ag-grid/utils/hooks/useAGGridRowHover.hook";
import { CategoryIcon } from "@/components/pages/dimensions/components/icons/CategoryIcon";

export const DimensionNameCellRenderer: FC<
  CustomCellRendererProps<DimensionsWithChildren | CategoryWithDimensionId>
> = (props) => {
  const dispatch = useAppDispatch();

  const { ref, hovered } = useAGGridRowHoverHook();

  const [editName, toggleEditName] = useToggle(false);

  const data = props?.data ?? dimensionPlaceholderData;
  const isCategory = isCategoryWithDimensionId(data);

  const { cannot } = useAppAbility();
  const canNotEditDimension = cannot("edit", "category");

  const nameClickHandler = useCallback(() => {
    if (canNotEditDimension) {
      return;
    }

    dispatch(editDimensionOrCategoryThunk(data));
  }, [dispatch, data, canNotEditDimension]);

  if (props.node.id === agGridGrandFooterColId) {
    return <AddDimensionButton variant="outlined" />;
  }

  if (!isCategory && props.node.footer) {
    return (
      <AddCategoryButtonIntoExistingDimension
        dimensionId={data.id}
        categories={data.categories}
        dimensionName={data.name}
      />
    );
  }

  return (
    <Stack
      ref={ref}
      spacing={1}
      direction="row"
      alignItems="center"
      justifyContent="center"
    >
      {isCategory && (
        <CategoryIcon color={!canNotEditDimension ? "primary" : undefined} />
      )}

      {editName ? (
        <DimensionCategoryEditField data={data} onSubmit={toggleEditName} />
      ) : (
        <Fragment>
          <TypographyWithTooltip
            variant="body2"
            title={data.name}
            onClick={nameClickHandler}
            color={!canNotEditDimension ? "primary" : undefined}
            sx={{ cursor: !canNotEditDimension ? "pointer" : "default" }}
          />

          {!isCategory && data?.categories?.length !== undefined && (
            <Tooltip
              arrow
              placement="top"
              title={
                data.categories.length > 1
                  ? `${data.categories.length} Categories`
                  : `${data.categories.length} Category`
              }
            >
              <Stack
                alignItems="center"
                width={24}
                height={24}
                minWidth={24}
                minHeight={24}
                borderRadius={10}
                justifyContent="center"
                bgcolor="grey.200"
              >
                <Typography
                  color="text.secondary"
                  fontWeight={500}
                  fontSize={12}
                >
                  {data.categories.length}
                </Typography>
              </Stack>
            </Tooltip>
          )}

          {!isCategory && data.status === "in_progress" && (
            <Chip
              label={
                <Tooltip
                  arrow
                  placement="right"
                  title="Processing is still in progress for this dimension. It will be usable in the Billing Explorer once processing is complete."
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="body2">Processing</Typography>
                  </Stack>
                </Tooltip>
              }
            />
          )}

          <Tooltip arrow placement="top" title="Edit Name">
            <IconButton
              size="small"
              sx={{
                ...iconButtonStyles,
                visibility: hovered ? "visible" : "hidden",
              }}
              disabled={canNotEditDimension}
              onClick={toggleEditName}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          {!isCategory && (
            <AddCategoryButtonIntoExistingDimension
              iconButton
              hovered={hovered}
              dimensionId={data.id}
              categories={data.categories}
              dimensionName={data.name}
            />
          )}
        </Fragment>
      )}
    </Stack>
  );
};

const dimensionPlaceholderData: DimensionWithCosts = {
  id: "",
  name: "",
  categories: [],
  total_cost: 0,
  status: "completed",
  filtered_resource_costs: 0,
};
const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
